import { Zap, CalendarClock, Timer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatNextRun } from "@/lib/format";

const JOB_META = {
  refresh_stock_prices: { label: "Live stock prices", detail: "yfinance batch · 50 symbols", live: true },
  refresh_crypto_prices: { label: "Live crypto prices", detail: "CoinGecko batch · 50 symbols", live: true },
  weekly_mute_digest: { label: "Weekly mute digest", detail: "Resend email fan-out", live: false },
  weekly_portfolio_health: { label: "Portfolio health email", detail: "Weekly copilot digest · Mon 09:30 UTC", live: false },
  live_prices_warm_start: { label: "Warm start", detail: "One-off boot refresh", live: true },
};

/**
 * /admin — lists every APScheduler job (from scheduler-status `jobs[]`),
 * highlighting the live price-refresh jobs with a pulsing LIVE dot.
 */
export default function SchedulerJobsCard({ jobs }) {
  if (!jobs?.length) return null;

  return (
    <Card className="glass-effect border-white/5" data-testid="scheduler-jobs-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-4">
          <CalendarClock className="w-3 h-3" /> Background jobs · {jobs.length} active
        </div>
        <div className="space-y-3">
          {jobs.map((job) => {
            const meta = JOB_META[job.id] || { label: job.name || job.id, detail: "", live: false };
            const isInterval = job.trigger?.startsWith("interval");
            return (
              <div
                key={job.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5"
                data-testid={`job-row-${job.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {meta.live ? (
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FFA3] opacity-60" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00FFA3]" />
                    </span>
                  ) : (
                    <Timer className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {meta.label}
                      {meta.live && (
                        <span className="text-[9px] font-bold tracking-widest text-[#00FFA3] border border-[#00FFA3]/30 bg-[#00FFA3]/10 rounded px-1.5 py-0.5">
                          LIVE
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 truncate">{meta.detail}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="text-xs font-bold mono text-gray-200"
                    data-testid={`job-next-run-${job.id}`}
                  >
                    {job.next_run_time ? formatNextRun(job.next_run_time) : "—"}
                  </div>
                  <div className="text-[10px] text-gray-600 mono flex items-center gap-1 justify-end">
                    {isInterval && <Zap className="w-2.5 h-2.5" />}
                    {job.trigger}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
